import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface OperacionData {
	aduana: string
	motivo: string
	sumaAsegurada: string
	capituloAduanero: string
	mercaderia: string
	documentoEmbarque: string
}

interface CaucionAduaneraOperacionScreenProps {
	onNext: (data: OperacionData) => void
	onBack: () => void
	data?: Partial<OperacionData>
}

export function CaucionAduaneraOperacionScreen({
	onNext,
	onBack,
	data,
}: CaucionAduaneraOperacionScreenProps) {
	const [formData, setFormData] = useState<OperacionData>({
		aduana: data?.aduana || '',
		motivo: data?.motivo || '',
		sumaAsegurada: data?.sumaAsegurada || '',
		capituloAduanero: data?.capituloAduanero || '',
		mercaderia: data?.mercaderia || '',
		documentoEmbarque: data?.documentoEmbarque || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const formatCurrency = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (!cleaned) return ''
		return Number(cleaned).toLocaleString('es-AR')
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.aduana.trim()) {
			newErrors.aduana = 'Ingresa la aduana'
		}
		if (!formData.motivo.trim()) {
			newErrors.motivo = 'Ingresa el motivo (siglas)'
		}
		if (!formData.sumaAsegurada.trim()) {
			newErrors.sumaAsegurada = 'Ingresa la suma asegurada'
		}
		if (!formData.mercaderia.trim()) {
			newErrors.mercaderia = 'Ingresa la descripción de la mercadería'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.aduana.trim() &&
		formData.motivo.trim() &&
		formData.sumaAsegurada.trim() &&
		formData.mercaderia.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos de la Operación
					</h2>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Aduana"
							labelPosition="internal"
							size="l"
							placeholder="Nombre de la aduana"
							value={formData.aduana}
							onChange={(e) => {
								setFormData({ ...formData, aduana: e.target.value })
								setErrors({ ...errors, aduana: '' })
							}}
							errorText={errors.aduana}
							required
						/>
						<InputText
							label="Motivo (Siglas)"
							labelPosition="internal"
							size="l"
							placeholder="Ej: IC, TR, etc."
							value={formData.motivo}
							onChange={(e) => {
								setFormData({ ...formData, motivo: e.target.value })
								setErrors({ ...errors, motivo: '' })
							}}
							errorText={errors.motivo}
							required
						/>
						<InputText
							label="Suma Asegurada"
							labelPosition="internal"
							size="l"
							placeholder="Monto en pesos"
							value={formData.sumaAsegurada}
							onChange={(e) => {
								const formatted = formatCurrency(e.target.value)
								setFormData({ ...formData, sumaAsegurada: formatted })
								setErrors({ ...errors, sumaAsegurada: '' })
							}}
							errorText={errors.sumaAsegurada}
							required
						/>
						<InputText
							label="Número de Capítulo Aduanero (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="Capítulo aduanero"
							value={formData.capituloAduanero}
							onChange={(e) => {
								setFormData({ ...formData, capituloAduanero: e.target.value })
							}}
						/>
						<div>
							<label className="font-parkinsans mb-2 block text-sm font-medium text-neutral-700">
								Mercadería (descripción) *
							</label>
							<textarea
								className="font-parkinsans w-full rounded-lg border border-neutral-300 px-4 py-3 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none"
								rows={3}
								placeholder="Descripción de la mercadería"
								value={formData.mercaderia}
								onChange={(e) => {
									setFormData({ ...formData, mercaderia: e.target.value })
									setErrors({ ...errors, mercaderia: '' })
								}}
							/>
							{errors.mercaderia && (
								<p className="mt-1 text-sm text-red-500">{errors.mercaderia}</p>
							)}
						</div>
						<InputText
							label="Factura Proforma / Conocimiento de Embarque / Guía Aérea (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="Número de documento"
							value={formData.documentoEmbarque}
							onChange={(e) => {
								setFormData({ ...formData, documentoEmbarque: e.target.value })
							}}
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar solicitud
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
